import { resolveUserLocation } from './geocoding';

// ─────────────────────────────────────────────
// Cookie consent gating (Iubenda "Marketing" — purpose 5)
// ─────────────────────────────────────────────

/**
 * Returns true when the visitor has accepted the Iubenda "Marketing" cookie
 * (purpose 5). 
 *
 * Consent is published by the Iubenda `onPreferenceExpressedOrNotNeeded`
 * callback in `views/structure/base.twig` as `window.freedomeConsent` and
 * mirrored to `localStorage['fd_consent']`. We read it at call time (i.e. when
 * the user performs the action), preferring the live global and falling back to
 * localStorage for the brief window before the callback runs on a fresh load.
 *
 * Default-deny: any missing or unreadable state counts as "no consent".
 */
export const hasMarketingCookieConsent = () => {
  const live = window.freedomeConsent;
  if (live && typeof live.marketing === 'boolean') {
    return live.marketing;
  }
  try {
    const stored = JSON.parse(localStorage.getItem('fd_consent'));
    return stored?.marketing === true;
  } catch {
    return false;
  }
};

// ─────────────────────────────────────────────
// Geolocation cost safeguards
// ─────────────────────────────────────────────

/**
 * Returns true when Braze geolocation must be restricted to users who have
 * accepted commercial communications. Read at call time from the WordPress
 * Customizer (Appearance → Customize → Braze), injected into
 * `window.freedomeConfig` server-side — so it can be flipped instantly
 * without a JS rebuild or theme deploy.
 *
 * Strict `=== true` check: any missing / non-boolean value falls back to
 * false (flag off = current authenticated-only gating applies). This keeps
 * behaviour safe if the option is absent or the bundle is stale.
 *
 * When enabled, consent is resolved via the
 * `/users/{user_id}/marketing-consents` API: a user counts as consented
 * when `email_subscribe` is `opted_in` or `subscribed`. API failures are
 * treated as "no consent" and not cached, so a transient outage retries
 * on the next session instead of blocking geolocation forever.
 */
const isMarketingConsentRequiredForGeolocation = () =>
  window.freedomeConfig?.requireMarketingConsentForGeolocation === true;

const DEFAULT_LOCATION_CACHE_TTL_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Returns the geolocation cache TTL in milliseconds, sourced from the
 * WordPress Customizer (Appearance → Customize → Braze) via
 * `window.freedomeConfig.geolocationCacheTtlDays`.
 *
 * Read at call time so the value can be tuned without a JS rebuild.
 * Falls back to 30 days when the config value is missing or not a
 * positive integer. The marketing-consent cache reuses the same TTL.
 */
const getLocationCacheTtlMs = () => {
  const days = Number(window.freedomeConfig?.geolocationCacheTtlDays);
  const safeDays = Number.isFinite(days) && days > 0
    ? days
    : DEFAULT_LOCATION_CACHE_TTL_DAYS;
  return safeDays * MS_PER_DAY;
};

const locationCacheKey = (userId) => `braze_location_cached_${userId}`;
const marketingConsentCacheKey = (userId) => `braze_marketing_consent_${userId}`;
const PENDING_SIGNUP_EMAIL_SUBSCRIPTION_KEY = 'braze_pending_signup_email_subscription';
const PENDING_SIGNUP_EMAIL_SUBSCRIPTION_TTL_MS = 30 * MS_PER_DAY;

const CONSENTED_EMAIL_SUBSCRIBE_STATES = new Set(['opted_in', 'subscribed']);

/**
 * Resolves the user's marketing-consent state, with a localStorage cache.
 *
 * @param {string|number} userId
 * @returns {Promise<boolean>}
 */
const hasMarketingConsent = async (userId) => {
  const cacheKey = marketingConsentCacheKey(userId);
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { value, timestamp } = JSON.parse(cached);
      const age = Date.now() - Number(timestamp);
      if (typeof value === 'boolean' && Number.isFinite(age) && age >= 0 && age < getLocationCacheTtlMs()) {
        return value;
      }
    } catch {
      // Malformed cache entry — fall through to refetch.
    }
  }

  const coreEndpoint = window.freedomeConfig?.endpoints?.core;
  if (!coreEndpoint) {
    return false;
  }

  try {
    const response = await fetch(
      `${coreEndpoint}/users/${encodeURIComponent(userId)}/marketing-consents`,
    );
    if (!response.ok) {
      throw new Error(`marketing-consents request failed: ${response.status}`);
    }

    const data = await response.json();
    const consented = CONSENTED_EMAIL_SUBSCRIBE_STATES.has(data?.email_subscribe);
    localStorage.setItem(cacheKey, JSON.stringify({ value: consented, timestamp: Date.now() }));
    return consented;
  } catch (error) {
    // console.warn('[freedome][braze] Could not resolve marketing consent:', error.message || error);
    return false;
  }
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/**
 * Returns true when the Braze global SDK is ready to use.
 *
 * The SDK script is injected and initialized by GTM in two separate steps:
 * `window.braze` (with all its methods) exists as soon as the script loads,
 * but until GTM calls `braze.initialize()` every SDK method is a silent
 * no-op and `getUser()` returns undefined. Checking only for the method
 * loses calls made in that window (changeUser was the main victim), so
 * readiness requires an actual user object.
 */
const isBrazeReady = () =>
  window.braze &&
  typeof window.braze.getUser === 'function' &&
  window.braze.getUser() != null;

/**
 * Safely executes a callback when Braze is available,
 * retrying with polling up to 12 s if needed.
 * @param {Function} callback – receives no arguments
 * @param {Function} [onTimeout] – called once if Braze never becomes ready
 */
export const whenBrazeReady = (callback, onTimeout) => {
  if (isBrazeReady()) {
    callback();
    return;
  }

  let attempts = 0;
  const maxAttempts = 24; // 12s / 500ms
  const interval = setInterval(() => {
    attempts++;
    if (isBrazeReady()) {
      clearInterval(interval);
      callback();
    } else if (attempts >= maxAttempts) {
      clearInterval(interval);
      if (onTimeout) onTimeout();
    }
  }, 500);
};

export const initBrazeAfterEngagement = (callback) => {
  window.dataLayer = window.dataLayer || [];
  let alreadyTriggered = false;

  const handleEngagement = (source) => {
    if (alreadyTriggered) return;
    alreadyTriggered = true;
    whenBrazeReady(callback, () => {
      // The SDK never initialized within the polling window (e.g. GTM is
      // still waiting on cookie consent before calling braze.initialize()).
      // Release the latch so a later trigger — the auto-detection poll below
      // runs until the SDK is ready — can retry instead of losing the call.
      alreadyTriggered = false;
    });
  };

  // 1. Fallback: once Braze is actually initialized (not just injected),
  // fire the callback. This bypasses the need for the DataLayer event if
  // it's failing, and has no time limit so a late braze.initialize() is
  // never missed.
  const brazeChecker = setInterval(() => {
    if (isBrazeReady()) {
      clearInterval(brazeChecker);
      handleEngagement('Auto-detection (window.braze found)');
    }
  }, 1000);

  // 2. The Listener + Logger
  const originalPush = window.dataLayer.push;
  window.dataLayer.push = function (obj) {
    if (obj && obj.event) {

      // Check for your specific event
      if (obj.event === 'user_engagement') {
        handleEngagement('DataLayer (user_engagement)');
      }
    }
    return originalPush.apply(window.dataLayer, arguments);
  };

  // 3. Check history immediately
  const historyMatch = window.dataLayer.find(ed => ed.event === 'user_engagement');
  if (historyMatch) {
    handleEngagement('DataLayer History');
  }
};

// Cache geocoding lookups to avoid duplicate requests for the same address input.
const regionLookupCache = new Map();

/**
 * Converts a price (number or numeric string) to an integer number of cents.
 * e.g. 249.66 → 24966, "249,66" → 24966
 * Returns null if the value cannot be parsed.
 * @param {number|string} value
 * @returns {number|null}
 */
const toCents = (value) => {
  if (value == null || value === '') return null;
  const cleaned = String(value).trim().replace(/[^0-9,.-]/g, '');
  if (!cleaned) return null;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');

  let normalized;
  if (lastComma > lastDot) {
    // Comma is decimal separator (e.g. 14.999,66)
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    // Dot is decimal separator (e.g. 14,999.66)
    normalized = cleaned.replace(/,/g, '');
  } else {
    normalized = cleaned;
  }

  const num = Number(normalized);
  if (Number.isNaN(num)) return null;
  return Math.round(num * 100);
};

/**
 * Converts a date value to an ISO 8601 string.
 * Returns the original value unchanged if it cannot be parsed as a date.
 * @param {string|Date} value
 * @returns {string}
 */
const toISODateString = (value) => {
  if (!value) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toISOString();
};

const extractAddressComponent = (results, componentType) => {
  for (const result of results) {
    if (!Array.isArray(result.address_components)) continue;

    for (const component of result.address_components) {
      if (component.types && component.types.includes(componentType)) {
        return component.long_name || null;
      }
    }
  }

  return null;
};

const resolveRegionFromAddress = async ({ zip_code: zipCode, city, country }) => {
  const apiKey = window.freedomeConfig?.googleGeocodingApiKey;
  const hasZipAndCountry = !!(zipCode && country);
  const query = hasZipAndCountry ? zipCode : city;

  if (!apiKey || !query) {
    return null;
  }

  const cacheKey = `${hasZipAndCountry ? 'zip' : 'city'}|${String(query).trim().toLowerCase()}|${String(country || '').trim().toLowerCase()}`;
  if (regionLookupCache.has(cacheKey)) {
    return regionLookupCache.get(cacheKey);
  }

  try {
    const fullQuery = hasZipAndCountry ? `${zipCode}, ${country}` : query;
    const url =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?address=${encodeURIComponent(fullQuery)}` +
      `&key=${encodeURIComponent(apiKey)}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Geocoding request failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.status !== 'OK' || !Array.isArray(data.results) || data.results.length === 0) {
      throw new Error(
        `Geocoding returned status: ${data.status}` +
        (data.error_message ? ` – ${data.error_message}` : '')
      );
    }

    const region = extractAddressComponent(data.results, 'administrative_area_level_1');
    regionLookupCache.set(cacheKey, region);

    return region;
  } catch (error) {
    // console.warn('[freedome][braze] Could not resolve region_address via geocoding:', error.message || error);
    regionLookupCache.set(cacheKey, null);
    return null;
  }
};

// ─────────────────────────────────────────────
// Standard User Attributes
// ─────────────────────────────────────────────

/**
 * Updates Braze **standard** user attributes based on the provided user object.
 * Only sets attributes that have values in the user object.
 *
 * Covers: first_name, last_name, email, phone, language, gender, dob
 *
 * @param {Object} user - The user object containing profile data
 */
export const updateBrazeUserAttributes = (user) => {
  initBrazeAfterEngagement(() => {
    applyBrazeUserAttributes(user);
  });
};

/**
 * Writes the standard user attributes on the current Braze user, assuming the
 * SDK is ready. Used directly by identifyAndSetBrazeUser, which needs the
 * writes to happen synchronously (before its data flush) and only after the
 * user identity has been confirmed.
 */
const applyBrazeUserAttributes = (user) => {
  const brazeUser = window.braze.getUser();

  if (user.first_name) {
    brazeUser.setFirstName(user.first_name);
  }

  if (user.last_name) {
    brazeUser.setLastName(user.last_name);
  }

  if (user.email) {
    brazeUser.setEmail(user.email);
  }

  if (user.phone) {
    brazeUser.setPhoneNumber(user.phone);
  }

  if (user.language) {
    brazeUser.setLanguage(user.language);
  }

  // gender and dob are "Marketing" attributes (https://app.clickup.com/t/869dg1yhr): only sync them once
  // the visitor has accepted the Iubenda Marketing cookie. The fields above
  // (name, email, phone, language) are essential and always sync.
  if (hasMarketingCookieConsent()) {
    if (user.gender) {
      const genderMap = {
        'female': window.braze.User.Genders.FEMALE,
        'f': window.braze.User.Genders.FEMALE,
        'male': window.braze.User.Genders.MALE,
        'm': window.braze.User.Genders.MALE,
        'other': window.braze.User.Genders.OTHER,
        'o': window.braze.User.Genders.OTHER,
        'not_applicable': window.braze.User.Genders.NOT_APPLICABLE,
        'n': window.braze.User.Genders.NOT_APPLICABLE,
        'prefer_not_to_say': window.braze.User.Genders.PREFER_NOT_TO_SAY,
        'p': window.braze.User.Genders.PREFER_NOT_TO_SAY,
      };
      const brazeGender = genderMap[user.gender.toLowerCase()] || window.braze.User.Genders.UNKNOWN;
      brazeUser.setGender(brazeGender);
    }

    if (user.date_of_birth) {
      const dob = new Date(user.date_of_birth);
      if (!isNaN(dob.getTime())) {
        // Braze uses 1-indexed months (1-12)
        brazeUser.setDateOfBirth(dob.getFullYear(), dob.getMonth() + 1, dob.getDate());
      }
    }
  }

  // console.log('[freedome][braze] User attributes updated');
};

// ─────────────────────────────────────────────
// Custom Attributes
// ─────────────────────────────────────────────

/**
 * Sets address-related custom attributes on the Braze user.
 * Called when a user updates their profile.
 *
 * Covers: city_address, province_address, region_address
 *
 * @param {Object} billingAddress - { city, country, zip_code }
 */
export const setBrazeAddressAttributes = (billingAddress) => {
  initBrazeAfterEngagement(() => {
    applyBrazeAddressAttributes(billingAddress);
  });
};

/**
 * Same as setBrazeAddressAttributes but assumes the SDK is ready — used by
 * identifyAndSetBrazeUser after the user identity has been confirmed.
 */
const applyBrazeAddressAttributes = (billingAddress) => {
  if (!billingAddress) return;
  // city_address, province_address and region_address are "Marketing"
  // attributes (https://app.clickup.com/t/869dg1yhr) — gated on Iubenda Marketing-cookie consent.
  if (!hasMarketingCookieConsent()) return;

  const brazeUser = window.braze.getUser();

  if (billingAddress.city) {
    brazeUser.setCustomUserAttribute('city_address', billingAddress.city);
  }

  if (billingAddress.country) {
    brazeUser.setCustomUserAttribute('province_address', billingAddress.country);
  }

  // region_address: resolve administrative region from zip code, or city fallback.
  (async () => {
    const resolvedRegion = await resolveRegionFromAddress(billingAddress);
    const fallbackRegion = billingAddress.city || null;
    const regionValue = resolvedRegion || fallbackRegion;

    if (regionValue) {
      brazeUser.setCustomUserAttribute('region_address', regionValue);
    }

    // console.log('[freedome][braze] Address custom attributes updated', {
    //   city: billingAddress.city || null,
    //   country: billingAddress.country || null,
    //   regionAddress: regionValue,
    // });
  })();
};

/**
 * Sets the favourite_activities array custom attribute.
 *
 * @param {number[]} entries - array of listing IDs on the wishlist
 */
export const setBrazeFavouriteActivities = (entries) => {
  initBrazeAfterEngagement(() => {
    applyBrazeFavouriteActivities(entries);
  });
};

/**
 * Same as setBrazeFavouriteActivities but assumes the SDK is ready — used by
 * identifyAndSetBrazeUser after the user identity has been confirmed.
 */
const applyBrazeFavouriteActivities = (entries) => {
  // favourite_activities is a "Marketing" attribute (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  const brazeUser = window.braze.getUser();
  brazeUser.setCustomUserAttribute('favourite_activities', entries.map(String));

  // console.log('[freedome][braze] favourite_activities updated');
};

/**
 * Sets Braze location attributes from a resolved location object.
 * - home_city  (standard attribute)
 * - country    (standard attribute) – falls back to marketplace country (IT / ES)
 * - region_location (custom attribute)
 * - province_location (custom attribute)
 * - last known location (lat/lng/accuracy) – set when coordinates are available
 *
 * A fallback country is only written when no real geocoded country was ever
 * stored (tracked via localStorage). Once a geocoded country has been set,
 * subsequent sessions will not overwrite it with a fallback.
 *
 * @param {{city: string|null, region: string|null, province: string|null, country: string, countryIsGeocoded: boolean, latitude: number|null, longitude: number|null, accuracy: number|null}} location
 */
export const setBrazeUserLocation = (location) => {
  const COUNTRY_SET_KEY = 'braze_country_set';

  initBrazeAfterEngagement(() => {
    const brazeUser = window.braze.getUser();

    // country is essential (https://app.clickup.com/t/869dg1yhr): always set it, using the geocoded value
    // when available or the marketplace fallback otherwise.
    if (location.countryIsGeocoded) {
      brazeUser.setCountry(location.country);
      localStorage.setItem(COUNTRY_SET_KEY, '1');
    } else if (location.country && !localStorage.getItem(COUNTRY_SET_KEY)) {
      // Fallback country — only set if no real geocoded country was ever stored
      brazeUser.setCountry(location.country);
    }

    // lat/lng, home_city, region_location and province_location are "Marketing"
    // data (https://app.clickup.com/t/869dg1yhr): only set them with Iubenda Marketing-cookie consent.
    if (hasMarketingCookieConsent()) {
      if (location.latitude != null && location.longitude != null) {
        brazeUser.setLastKnownLocation(
          location.latitude,
          location.longitude,
          location.accuracy ?? undefined,
        );
      }

      if (location.city) {
        brazeUser.setHomeCity(location.city);
      }

      if (location.region) {
        brazeUser.setCustomUserAttribute('region_location', location.region);
      }

      if (location.province) {
        brazeUser.setCustomUserAttribute('province_location', location.province);
      }
    }

    // console.log('[freedome][braze] Location attributes updated:', location);
  });
};

/**
 * Resolves the user's location via Google APIs and syncs it to Braze.
 * Called from the auth flow after the user has been identified, so we only
 * pay for Google Geolocation/Geocoding calls on authenticated visitors.
 *
 * Cost safeguards:
 * - Caller must have an authenticated user object (no-op otherwise)
 * - Per-user cache in localStorage to avoid re-calling Google APIs across
 *   sessions. TTL defaults to 30 days and is configurable from the WP
 *   Customizer (Appearance → Customize → Braze → Geolocation cache TTL).
 *   When the geolocation cache is fresh, we skip everything (including
 *   the marketing-consent API call) for the rest of the TTL.
 * - Optional marketing-consent gate, toggled from the WP Customizer
 *   (Appearance → Customize → Braze) and read via window.freedomeConfig.
 *   When on, consent is fetched from `/users/{user_id}/marketing-consents`
 *   and cached per-user (same TTL) so we don't refetch on every page load.
 * - Google Geolocation/Geocoding is called only after Braze has
 *   initialized (SDK ready + engagement signal). If Braze never boots
 *   (e.g. blocked by an ad-blocker), the callback never fires: no Google
 *   API call, no cache write — so the next session retries instead of
 *   losing the data.
 *
 * @param {Object} user - the identified user object (must have a numeric id)
 */
export const resolveAndSyncBrazeUserLocation = async (user) => {
  if (!user || !user.id) return;

  const cacheKey = locationCacheKey(user.id);
  const cachedTimestamp = localStorage.getItem(cacheKey);
  if (cachedTimestamp) {
    const age = Date.now() - Number(cachedTimestamp);
    if (!Number.isNaN(age) && age >= 0 && age < getLocationCacheTtlMs()) {
      // console.log('[freedome][braze] Skipping geolocation: cache hit');
      return;
    }
  }

  // Geolocation enrichment (lat/lng, home_city, region/province) is "Marketing"
  // data and calls paid Google APIs, so it only runs with Iubenda Marketing-
  // cookie consent. Without it we still set the essential `country` from the
  // marketplace fallback (no API call), then stop. We deliberately don't write
  // the location cache here, so a later session retries the full resolution
  // once consent is granted.
  if (!hasMarketingCookieConsent()) {
    const fallbackCountry = window.freedomeConfig?.baseLocale === 'es' ? 'ES' : 'IT';
    setBrazeUserLocation({
      city: null,
      region: null,
      province: null,
      country: fallbackCountry,
      countryIsGeocoded: false,
      latitude: null,
      longitude: null,
      accuracy: null,
    });
    return;
  }

  if (isMarketingConsentRequiredForGeolocation()) {
    const consented = await hasMarketingConsent(user.id);
    if (!consented) {
      // console.log('[freedome][braze] Skipping geolocation: marketing consent required');
      return;
    }
  }

  initBrazeAfterEngagement(() => {
    resolveUserLocation().then((location) => {
      setBrazeUserLocation(location);
      localStorage.setItem(cacheKey, String(Date.now()));
    });
  });
};

/**
 * Sets the marketplace custom attribute based on the current domain.
 * Value will be 'it' or 'es'.
 *
 * Assumes the SDK is ready — only called from identifyAndSetBrazeUser after
 * the user identity has been confirmed.
 */
const applyBrazeMarketplace = () => {
  const brazeUser = window.braze.getUser();
  const marketplace = window.freedomeConfig.baseLocale === 'es' ? 'es' : 'it';
  brazeUser.setCustomUserAttribute('marketplace', marketplace);

  // console.log(`[freedome][braze] marketplace set to ${marketplace}`);
};

/**
 * Sets the customer_id numeric custom attribute.
 *
 * Assumes the SDK is ready — only called from identifyAndSetBrazeUser after
 * the user identity has been confirmed.
 *
 * @param {number} customerId - numeric user id without locale prefix
 */
const applyBrazeCustomerId = (customerId) => {
  const brazeUser = window.braze.getUser();
  brazeUser.setCustomUserAttribute('customer_id', Number(customerId));

  // console.log('[freedome][braze] customer_id set');
};

/**
 * Derives the auth_method from the user.sub string.
 * e.g. "auth0|67d1941c..." → "email_password"
 *      "google-oauth2|123..." → "google-oauth2"
 *      "facebook|456..." → "facebook"
 *
 * @param {string} sub - Auth0 subject claim (user.sub)
 * @returns {string|null}
 */
export const getAuthMethodFromSub = (sub) => {
  if (!sub) return null;
  const provider = sub.split('|')[0];
  return provider === 'auth0' ? 'email_password' : provider;
};

/**
 * Sets auth_method custom attribute.
 * Possible values: email_password, facebook, apple, google-oauth2
 *
 * @param {string} method
 */
export const setBrazeAuthMethod = (method) => {
  if (!method) return;

  initBrazeAfterEngagement(() => {
    const brazeUser = window.braze.getUser();
    brazeUser.setCustomUserAttribute('auth_method', method);

    // console.log(`[freedome][braze] auth_method set to ${method}`);
  });
};

/**
 * Adds or removes the current user from the marketing (commercial
 * communications) Braze subscription group, mirroring their email-subscribe
 * choice. This is the same essential commercial-communications preference the
 * user explicitly expressed, so it syncs alongside setEmailNotificationSubscriptionType
 * and is NOT gated on Marketing-cookie consent.
 *
 * The subscription-group ID is environment-specific (each Braze workspace has
 * its own), resolved server-side in Env::getBrazeMarketingSubscriptionGroupId()
 * and injected as window.freedomeConfig.brazeMarketingSubscriptionGroupId — so
 * the production ID can be swapped without a JS rebuild. When the ID is missing
 * (not yet configured for an environment) we skip the call.
 *
 * Must be called inside initBrazeAfterEngagement (SDK ready).
 *
 * @param {object} brazeUser - window.braze.getUser()
 * @param {boolean} isSubscribed
 */
const setBrazeMarketingSubscriptionGroup = (brazeUser, isSubscribed) => {
  const groupId = window.freedomeConfig?.brazeMarketingSubscriptionGroupId;
  if (!groupId) return;

  if (isSubscribed) {
    brazeUser.addToSubscriptionGroup(groupId);
  } else {
    brazeUser.removeFromSubscriptionGroup(groupId);
  }

  // console.log(`[freedome][braze] marketing subscription group ${isSubscribed ? 'added' : 'removed'} (${groupId})`);
};

const applyBrazeEmailSubscribe = (brazeUser, isSubscribed) => {
  if (isSubscribed) {
    brazeUser.setEmailNotificationSubscriptionType(
      window.braze.User.NotificationSubscriptionTypes.SUBSCRIBED
    );
  } else {
    brazeUser.setEmailNotificationSubscriptionType(
      window.braze.User.NotificationSubscriptionTypes.UNSUBSCRIBED
    );
  }

  setBrazeMarketingSubscriptionGroup(brazeUser, isSubscribed);
};

/**
 * Sets email_subscribe standard attribute.
 * Value should be 'opted_in', 'subscribed' or 'unsubscribed'.
 *
 * @param {boolean} isSubscribed
 */
export const setBrazeEmailSubscribe = (isSubscribed) => {
  initBrazeAfterEngagement(() => {
    const brazeUser = window.braze.getUser();
    applyBrazeEmailSubscribe(brazeUser, isSubscribed);

    // console.log(`[freedome][braze] email_subscribe set to ${isSubscribed ? 'subscribed' : 'unsubscribed'}`);
  });
};

/**
 * localStorage, not sessionStorage: GTM initializes the SDK on engagement
 * (first click, or ~10s), so a visitor who signs up and closes the tab is gone
 * before Braze is ready. Persisting lets the write be retried on a later visit.
 */
export const queueBrazeEmailSubscriptionAfterSignup = (email, isSubscribed) => {
  localStorage.setItem(PENDING_SIGNUP_EMAIL_SUBSCRIPTION_KEY, JSON.stringify({
    email: String(email).trim().toLowerCase(),
    isSubscribed: !!isSubscribed,
    queuedAt: Date.now(),
  }));
};

const syncPendingBrazeEmailSubscription = (user) => {
  const pendingValue = localStorage.getItem(PENDING_SIGNUP_EMAIL_SUBSCRIPTION_KEY);
  if (!pendingValue) return;

  let pending;
  try {
    pending = JSON.parse(pendingValue);
  } catch {
    localStorage.removeItem(PENDING_SIGNUP_EMAIL_SUBSCRIPTION_KEY);
    return;
  }

  const isExpired =
    !Number.isFinite(pending.queuedAt) ||
    Date.now() - pending.queuedAt > PENDING_SIGNUP_EMAIL_SUBSCRIPTION_TTL_MS;
  if (typeof pending.isSubscribed !== 'boolean' || isExpired) {
    localStorage.removeItem(PENDING_SIGNUP_EMAIL_SUBSCRIPTION_KEY);
    return;
  }

  // Another account on this browser: leave it for its owner's next visit.
  const identifiedEmail = String(user.email || '').trim().toLowerCase();
  if (!identifiedEmail || pending.email !== identifiedEmail) return;

  applyBrazeEmailSubscribe(window.braze.getUser(), pending.isSubscribed);
  window.braze.requestImmediateDataFlush((success) => {
    if (success) {
      localStorage.removeItem(PENDING_SIGNUP_EMAIL_SUBSCRIPTION_KEY);
    }
  });
};

/**
 * Sets the subscribed_to_newsletter_from_box custom attribute.
 *
 * @param {boolean} value
 */
export const setBrazeNewsletterFromBox = (value) => {
  // subscribed_to_newsletter_from_box is a "Marketing" attribute (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  initBrazeAfterEngagement(() => {
    const brazeUser = window.braze.getUser();
    brazeUser.setCustomUserAttribute('subscribed_to_newsletter_from_box', !!value);

    // console.log(`[freedome][braze] subscribed_to_newsletter_from_box set to ${value}`);
  });
};

// ─────────────────────────────────────────────
// Custom Events
// ─────────────────────────────────────────────

/**
 * Logs the newsletter_subscribed custom event and sets subscription attributes.
 * Works for both anonymous and identified users via the Braze SDK.
 *
 * @param {'footer'|'checkout'} source - where the subscription happened
 * @param {string} [email] - subscriber email (used for anonymous users)
 */
export const logBrazeNewsletterSubscribed = (source, email) => {
  initBrazeAfterEngagement(() => {
    if (email) {
      window.braze.getUser().setEmail(email);
    }

    window.braze.getUser().setEmailNotificationSubscriptionType(
      window.braze.User.NotificationSubscriptionTypes.SUBSCRIBED
    );

    // Also add the user to the marketing subscription group (essential
    // commercial-communications preference, always synced — not cookie-gated).
    setBrazeMarketingSubscriptionGroup(window.braze.getUser(), true);

    // Only flag the attribute when subscribing from the newsletter box on the page,
    // not from checkout or other sources. subscribed_to_newsletter_from_box is a
    // "Marketing" attribute (https://app.clickup.com/t/869dg1yhr), so it's gated on Marketing-cookie consent.
    // The email + subscription type above are essential (the user's commercial-
    // communications preference) and always sync.
    if (source === 'footer' && hasMarketingCookieConsent()) {
      window.braze.getUser().setCustomUserAttribute('subscribed_to_newsletter_from_box', true);
    }

    window.braze.logCustomEvent('newsletter_subscribed', {
      source,
      date: new Date().toISOString(),
    });

    // console.log(`[freedome][braze] Event: newsletter_subscribed (source: ${source})`);
  });
};

/**
 * Logs the account_registration_completed custom event.
 *
 * @param {string} method - 'email_password' | 'facebook' | 'apple' | 'google-oauth2'
 * @param {'menu'|'checkout'} referrer - where the registration was completed
 */
export const logBrazeAccountRegistration = (method, referrer = 'menu') => {
  const now = new Date();
  initBrazeAfterEngagement(() => {
    window.braze.logCustomEvent('account_registration_completed', {
      method: method,
      referrer: referrer,
      date: now.toISOString(),
      'month-year': `${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}`,
    });

    // console.log(`[freedome][braze] Event: account_registration_completed (${method}, ${referrer})`);
  });
};

/**
 * Logs the signed_in custom event.
 */
export const logBrazeSignedIn = () => {
  // signed_in is a "Marketing" event (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  const now = new Date();
  initBrazeAfterEngagement(() => {
    window.braze.logCustomEvent('signed_in', {
      date: now.toISOString(),
    });

    // console.log('[freedome][braze] Event: signed_in');
  });
};

/**
 * Logs the pageview custom event.
 *
 * @param {string} pageName
 * @param {string|null} category - micro category
 * @param {string|null} macroCategory - macro category
 * @param {Object} [extra] - optional extra properties
 * @param {string} [extra.productType] - voucher | booking | giftcard (checkout only)
 */
export const logBrazePageview = (pageName, category, macroCategory, extra = {}) => {
  // pageview is a "Marketing" event (https://app.clickup.com/t/869dg1yhr) — fires on every page load.
  if (!hasMarketingCookieConsent()) return;

  initBrazeAfterEngagement(() => {
    const domain = window.location.hostname;
    const properties = { page_name: pageName, domain };
    if (category) properties.category = category;
    if (macroCategory) properties.macro_category = macroCategory;
    if (extra.productType) properties.product_type = extra.productType;

    // Listing page properties
    if (extra.activityTitle) properties.activity_title = extra.activityTitle;
    if (extra.activityImageUrl) properties.activity_image_url = extra.activityImageUrl;
    if (extra.activityUrl) properties.activity_url = extra.activityUrl;
    if (extra.activityRating !== undefined && extra.activityRating !== null) {
      properties.activity_rating = extra.activityRating;
    }
    if (extra.activityLocation) properties.activity_location = extra.activityLocation;
    if (extra.activityPrice !== undefined && extra.activityPrice !== null) {
      const activityPriceCents = toCents(extra.activityPrice);
      if (activityPriceCents !== null) properties.activity_price = activityPriceCents;
    }

    // Checkout page properties
    if (extra.bookingDatetime) properties.booking_datetime = extra.bookingDatetime;
    if (extra.bookingParticipants) properties.booking_participants = extra.bookingParticipants;
    if (extra.cartTotal !== undefined && extra.cartTotal !== null) {
      const cartTotalCents = toCents(extra.cartTotal);
      if (cartTotalCents !== null) properties.cart_total = cartTotalCents;
    }

    window.braze.logCustomEvent('pageview', properties);
  });
};

/**
 * Logs the added_to_favourite custom event.
 *
 * @param {Object} params
 * @param {string} params.pageName
 * @param {string|null} params.category
 * @param {string|null} params.macroCategory
 * @param {string|null} params.activityName
 */
export const logBrazeAddedToFavourite = ({ pageName, category, macroCategory, activityName }) => {
  // added_to_favourite is a "Marketing" event (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  initBrazeAfterEngagement(() => {
    const properties = { page_name: pageName || window.location.pathname };
    if (category) properties.category = category;
    if (macroCategory) properties.macro_category = macroCategory;
    if (activityName) properties.activity_name = activityName;

    window.braze.logCustomEvent('added_to_favourite', properties);
    // console.log('[freedome][braze] Event: added_to_favourite');
  });
};

/**
 * Logs the removed_to_favourite custom event.
 *
 * @param {Object} params
 * @param {string} params.pageName
 * @param {string|null} params.category
 * @param {string|null} params.macroCategory
 * @param {string|null} params.activityName
 */
export const logBrazeRemovedFromFavourite = ({ pageName, category, macroCategory, activityName }) => {
  // removed_to_favourite is a "Marketing" event (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  initBrazeAfterEngagement(() => {
    const properties = { page_name: pageName || window.location.pathname };
    if (category) properties.category = category;
    if (macroCategory) properties.macro_category = macroCategory;
    if (activityName) properties.activity_name = activityName;

    window.braze.logCustomEvent('removed_to_favourite', properties);
    // console.log('[freedome][braze] Event: removed_to_favourite');
  });
};

/**
 * Logs the clicked_on_banner_website custom event.
 *
 * @param {string} pageName
 * @param {string} bannerName
 */
export const logBrazeBannerClick = (pageName, bannerName) => {
  // clicked_on_banner_website is a "Marketing" event (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  initBrazeAfterEngagement(() => {
    window.braze.logCustomEvent('clicked_on_banner_website', {
      page_name: pageName || window.location.pathname,
      banner_name: bannerName,
    });
    // console.log(`[freedome][braze] Event: clicked_on_banner_website (${bannerName})`);
  });
};

/**
 * Logs the written_review custom event.
 *
 * @param {number} stars - review score
 * @param {string} activityTitle - the title of the reviewed activity
 * @param {string} title - review title
 * @param {string} description - review description
 * @param {string} coverImageUrl - full URL of the listing cover image
 * @param {string} activitySlug - the slug of the reviewed activity
 */
export const logBrazeWrittenReview = (stars, activityTitle, title, description, coverImageUrl, activitySlug) => {
  // written_review is a "Marketing" event (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  initBrazeAfterEngagement(() => {
    const properties = { stars: Number(stars) };
    if (activityTitle) properties.activity_title = activityTitle;
    if (title) properties.title = title;
    if (description) properties.description = description;
    if (coverImageUrl) properties.cover_image_url = coverImageUrl;
    if (activitySlug) properties.activity_slug = activitySlug;

    window.braze.logCustomEvent('written_review', properties);
    // console.log(`[freedome][braze] Event: written_review (${stars} stars, ${activityTitle})`);
  });
};

/**
 * Logs the profile_completed custom event.
 */
export const logBrazeProfileCompleted = () => {
  // profile_completed is a "Marketing" event (https://app.clickup.com/t/869dg1yhr).
  if (!hasMarketingCookieConsent()) return;

  initBrazeAfterEngagement(() => {
    window.braze.logCustomEvent('profile_completed');
    // console.log('[freedome][braze] Event: profile_completed');
  });
};

/**
 * Logs the completed_experience custom event.
 *
 * @param {Object} params
 * @param {string} params.category
 * @param {string} params.macroCategory
 * @param {string} params.activityName
 * @param {Object} params.location - { city, region, country }
 */
export const logBrazeCompletedExperience = ({ category, macroCategory, activityName, location }) => {
  initBrazeAfterEngagement(() => {
    const properties = {};
    if (category) properties.category = category;
    if (macroCategory) properties.macro_category = macroCategory;
    if (activityName) properties.activity_name = activityName;
    if (location) {
      if (location.city) properties['location.city'] = location.city;
      if (location.region) properties['location.region'] = location.region;
      if (location.country) properties['location.country'] = location.country;
    }

    window.braze.logCustomEvent('completed_experience', properties);
    // console.log('[freedome][braze] Event: completed_experience');
  });
};

// ─────────────────────────────────────────────
// Purchase Events
// ─────────────────────────────────────────────

/**
 * Logs a Braze purchase event for booking, voucher or giftcard.
 *
 * Uses window.braze.logPurchase(productId, price, currencyCode, quantity, properties)
 *
 * @param {string}  productType - 'booking' | 'voucher' | 'giftcard'
 * @param {Object}  params
 * @param {string}  params.orderId
 * @param {number}  params.valueOrder - total price
 * @param {string}  [params.currencyCode='EUR']
 * @param {string}  [params.category]
 * @param {string}  [params.activityName]
 * @param {string}  [params.creationDate]
 * @param {string}  [params.bookingDate]    - only for booking
 * @param {Object}  [params.location]       - { city, region, country }
 * @param {string}  [params.macroCategory]
 * @param {string}  [params.status]
 * @param {boolean} [params.discountRedeemed]
 * @param {string}  [params.cohort]
 * @param {number}  [params.repetition]
 */
export const logBrazePurchase = (productType, {
  orderId,
  valueOrder,
  currencyCode = 'EUR',
  category,
  activityName,
  creationDate,
  bookingDate,
  location,
  macroCategory,
  status,
  discountRedeemed,
  cohort,
  repetition,
} = {}) => {
  initBrazeAfterEngagement(() => {
    const properties = {};
    if (category) properties.category = category;
    if (activityName) properties.activity_name = activityName;
    if (creationDate) properties.creation_date = toISODateString(creationDate);
    if (bookingDate) properties.booking_date = toISODateString(bookingDate);
    if (macroCategory) properties['macro-category'] = macroCategory;
    if (status) properties.status = status;
    if (typeof discountRedeemed !== 'undefined') properties.discount_redeemed = discountRedeemed;
    if (cohort) properties.cohort = cohort;
    if (typeof repetition !== 'undefined') properties.repetition = repetition;
    if (location) {
      if (location.city) properties['location.city'] = location.city;
      if (location.region) properties['location.region'] = location.region;
      if (location.country) properties['location.country'] = location.country;
    }

    // productType acts as the product ID in Braze
    window.braze.logPurchase(
      productType,          // productId
      toCents(valueOrder) ?? 0,
      currencyCode,
      1,                    // quantity
      properties
    );

    // console.log(`[freedome][braze] Purchase: ${productType} – order ${orderId}, value ${valueOrder}`);
  });
};

// ─────────────────────────────────────────────
// Identify & init
// ─────────────────────────────────────────────

const BRAZE_IDENTITY_MAX_ATTEMPTS = 10;
const BRAZE_IDENTITY_RETRY_DELAY_MS = 500;

/**
 * Makes sure the Braze SDK is operating as `brazeUserId` before running
 * `onIdentified`.
 *
 * changeUser() can be silently lost (e.g. when called in the window between
 * the GTM-injected script appearing and braze.initialize() running), and any
 * attribute written while the switch has not happened lands on the anonymous
 * device profile instead of the customer's — producing Braze profiles with an
 * empty external id but a filled customer_id/email. So the current user id is
 * read back to confirm the switch, retrying changeUser a few times; if it
 * never confirms, nothing is written at all.
 */
const ensureBrazeIdentity = (brazeUserId, onIdentified, attempt = 0) => {
  window.braze.getUser().getUserId((currentUserId) => {
    if (currentUserId === brazeUserId) {
      onIdentified();
      return;
    }

    if (attempt >= BRAZE_IDENTITY_MAX_ATTEMPTS) {
      return;
    }

    window.braze.changeUser(brazeUserId);
    setTimeout(() => {
      ensureBrazeIdentity(brazeUserId, onIdentified, attempt + 1);
    }, BRAZE_IDENTITY_RETRY_DELAY_MS);
  });
};

/**
 * Identifies a user in Braze and sets core attributes.
 *
 * On every page load: external_id (via changeUser), customer_id.
 * On first-ever login (no prior sync stored in localStorage): also pushes
 * billing address and favourite activities so Braze has initial data.
 * Subsequent updates to those attributes happen only on the profile page.
 *
 * All attribute writes happen only after the identity switch is confirmed
 * (see ensureBrazeIdentity), and the first-sync flags are stored only after
 * Braze confirms the data was flushed to its servers.
 *
 * @param {Object} user - The user object containing profile data
 */
export const identifyAndSetBrazeUser = (user) => {
  initBrazeAfterEngagement(() => {
    const prefix = window.freedomeConfig.baseLocale === 'es' ? 'ES_' : 'IT_';
    const brazeUserId = `${prefix}${user.id}`;

    ensureBrazeIdentity(brazeUserId, () => {
      // console.log(`[freedome][braze] Braze user identified: ${brazeUserId}`);

      // Custom attributes – customer_id + marketplace (lightweight, always safe to set)
      applyBrazeCustomerId(user.id);
      applyBrazeMarketplace();

      syncPendingBrazeEmailSubscription(user);

      // On first-ever identification, seed Braze with the user's profile data.
      // Essential identity (name, email, phone, language) syncs immediately; the
      // "Marketing" attributes inside applyBrazeUserAttributes (gender, dob) and
      // the Marketing-only setters (address, favourites) are gated on Marketing-
      // cookie consent. We track the two with separate flags so the Marketing
      // attributes still seed on a later visit once consent is granted, instead of
      // being permanently skipped because the essential sync ran first.
      const essentialSyncKey = `braze_profile_synced_${user.id}`;
      const marketingSyncKey = `braze_profile_marketing_synced_${user.id}`;
      const needsEssentialSync = !localStorage.getItem(essentialSyncKey);
      const needsMarketingSync =
        hasMarketingCookieConsent() && !localStorage.getItem(marketingSyncKey);

      if (!needsEssentialSync && !needsMarketingSync) {
        return;
      }

      applyBrazeUserAttributes(user);

      if (needsMarketingSync) {
        if (user.billing_address) {
          applyBrazeAddressAttributes(user.billing_address);
        }

        if (user.wishlist && user.wishlist.length > 0) {
          applyBrazeFavouriteActivities(user.wishlist);
        }
      }

      // The SDK flushes on an interval, so a navigation right after login
      // (e.g. a checkout redirect) can drop these writes even though the
      // changeUser above already went out — leaving an identified profile
      // with no email. Mark the sync as done only once Braze confirms the
      // flush; a lost write is then retried on the next page load.
      window.braze.requestImmediateDataFlush((success) => {
        if (!success) return;
        if (needsEssentialSync) {
          localStorage.setItem(essentialSyncKey, '1');
        }
        if (needsMarketingSync) {
          localStorage.setItem(marketingSyncKey, '1');
        }
        // console.log('[freedome][braze] Initial profile attributes synced');
      });
    });
  });
};
